import React, { useEffect, useRef } from "react";
import DataTable from "react-data-table-component";
import { useDispatch, useSelector } from "react-redux";

import { POUND_SIGN } from "@/Constants/common";
import {
  deSelectAllListings,
  fetchContractShifts,
  setLoading,
  updateSelectedShifts,
  updateShiftListSorting,
  updateShiftsPagination,
} from "@/reducers/clientcontractdetail";
import {
  getActionByHtml,
  getColumnNormalHtml,
} from "@/utilities/datatable/columnFunctions";
import useDebounceScroll from "@/utilities/hooks/useDebounceScroll";
import { commonFormatTime } from "@/utilities/utils";

import TableLoader from "../../TableLoader";
import TableNoContent from "../../TableNoContent";
import WorkedStatusButton from "../candidates/Listing/Worked/WorkedStatusButton";
import ClientPayPopover from "../dataTables/ClientPayPopover";

/**
 * ShiftListing component function for rendering a table of contract shifts.
 *
 * @return {JSX.Element} The rendered table of contract shifts
 */
const ShiftListing = () => {
  const {
    isShiftLoading,
    contractShiftsLists,
    shiftsPagination,
    filters,
    contractId,
    toggleSelected,
    contractDetails,
    shiftListSorting,
  } = useSelector((state) => state.clientContractDetail);
  const dispatch = useDispatch();

  const firstRender = useRef(true);
  useEffect(() => {
    // updating shifts on shift date change
    if (contractId == null) return;
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }

    dispatch(
      updateShiftsPagination({
        ...shiftsPagination,
        currentPage: 1,
        isLastPage: false,
      }),
    );
    dispatch(
      fetchContractShifts({
        contract_id: contractId,
        dates: filters.shiftDates.join(","),
      }),
    );
  }, [filters.shiftDates]);
  const columns = [
    {
      name: "Date",
      sortable: true,
      sortField: "contract_date",
      // sortFunction: (rowA, rowB) =>
      //   sortByDate(
      //     moment(rowA.contract_date, "DD/MM/YYYY hh:mm").format("DD/MM/YYYY"),
      //     moment(rowB.contract_date, "DD/MM/YYYY hh:mm").format("DD/MM/YYYY"),
      //   ),
      selector: (row) => (
        <div className="flex items-center">
          {getColumnNormalHtml(row.contract_date)} &nbsp;&nbsp;
          {row.timesheet_status === "Terminated"
            ? getColumnNormalHtml(`(${row.timesheet_status})`, "!text-red")
            : null}
        </div>
      ),
      width: "16%",
    },

    {
      name: "Actioned By",
      selector: (row) => (
        <div className="flex flex-col" data-tag="allowRowEvents">
          {getActionByHtml(
            row.timesheet_status === "Authorized"
              ? row?.authorised_by_name
              : row?.timesheet_status == "Rejected"
                ? row.rejected_by_name
                : row.timesheet_status == "Terminated"
                  ? row.terminated_by_name
                  : "-",
            row.timesheet_status === "Authorized"
              ? row?.authorised_at
              : row?.timesheet_status == "Rejected"
                ? row.rejected_at
                : row?.timesheet_status == "Terminated"
                  ? row.terminated_at
                  : "-",
            row.timesheet_status === "Authorized"
              ? row?.authorised_from
              : row?.timesheet_status == "Rejected"
                ? row.rejected_from
                : row?.timesheet_status == "Terminated"
                  ? row.terminated_from
                  : "-",
            "",
          )}
        </div>
      ),
      width: "12%",
    },
    {
      name: "Start",
      sortable: true,
      sortField: "contract_start_time",
      // sortFunction: (rowA, rowB) =>
      //   sortByTime(
      //     moment(rowA.start_time, "HH:mm:ss").format("HH:mm"),
      //     moment(rowB.start_time, "HH:mm:ss").format("HH:mm"),
      //   ),
      selector: (row) =>
        getColumnNormalHtml(commonFormatTime(row.start_time, "HH:mm:ss")),
    },
    {
      name: "End",
      sortable: true,
      sortField: "contract_end_time",
      // sortFunction: (rowA, rowB) =>
      //   sortByTime(
      //     moment(rowA.end_time, "HH:mm:ss").format("HH:mm"),
      //     moment(rowB.end_time, "HH:mm:ss").format("HH:mm"),
      //   ),
      selector: (row) =>
        getColumnNormalHtml(commonFormatTime(row.end_time, "HH:mm:ss")),
    },
    {
      name: "Break",
      sortable: true,
      sortField: "un_paid_break_hour",
      // sortFunction: (rowA, rowB) =>
      //   sortByTime(
      //     moment(rowA.break_time, "HH:mm:ss").format("HH:mm"),
      //     moment(rowB.break_time, "HH:mm:ss").format("HH:mm"),
      //   ),
      selector: (row) =>
        getColumnNormalHtml(commonFormatTime(row.break_time, "HH:mm:ss")),
    },
    {
      name: "Admin",
      sortable: true,
      sortField: "contract_admin_time",
      // sortFunction: (rowA, rowB) =>
      //   sortByTime(
      //     moment(rowA.admin_time, "HH:mm:ss").format("HH:mm"),
      //     moment(rowB.admin_time, "HH:mm:ss").format("HH:mm"),
      //   ),
      selector: (row) =>
        getColumnNormalHtml(commonFormatTime(row.admin_time, "HH:mm:ss")),
    },
    {
      name: "Visit",
      sortable: false,
      sortField: "no_of_visit",
      selector: (row) =>
        getColumnNormalHtml(Number(row.no_of_visit, "HH:mm:ss")),
    },
    {
      name: "Unit",
      sortable: false,
      // sortFunction: (rowA, rowB) => sortByNumber(rowA, rowB, "unit"),
      selector: (row) =>
        row.unit !== "-"
          ? getColumnNormalHtml(Number(`${row.unit}`).toFixed(2))
          : "0.00",
      // getColumnNormalHtml(row.unit ? row.unit : "-"),
      width: "7.5%",
    },
    {
      name: `Charge Rate (${POUND_SIGN})`,
      sortable: false,
      // sortFunction: (rowA, rowB) => sortByNumber(rowA, rowB, "shift_rate"),
      selector: (row) => (
        <ClientPayPopover
          pay_arr={row?.client_pay_arr}
          shift_rate={
            Number(row.shift_rate) +
            Number(row.cover_request_awarded_reward || 0) -
            Number(row.cover_request_awarded_penalty || 0)
          }
          is_substitute_contract={
            row?.cover_request_awarded_reward != 0
              ? 1
              : row?.cover_request_awarded_penalty != 0
                ? 1
                : 0
          }
          cover_request_awarded_penalty={
            row?.cover_request_awarded_penalty || 0
          }
          cover_request_awarded_reward={row?.cover_request_awarded_reward || 0}
          content={(content) => getColumnNormalHtml(content, "cursor-pointer")}
        />
      ),
    },
    {
      name: "Status",
      sortable: false,
      // sortFunction: (rowA, rowB) => sortByText(rowA, rowB, "timesheet_status"),
      selector: (row) => (
        <WorkedStatusButton
          timesheet_status={row.timesheet_status}
          contract_ref_no={contractDetails.contract_ref_no}
          btnClass="h-[24px] cursor-pointer rounded-md border px-2 text-[14px] font-medium leading-[24px]"
        />
      ),
    },
  ];
  const dataTableRef = useRef();
  /**
   * Handles the scroll event on a table element and dispatches a fetchContractShifts action if at the bottom of the table.
   */

  const handleDebounceScroll = useDebounceScroll(() =>
    dispatch(fetchContractShifts({})),
  );
  useEffect(() => {
    if (dataTableRef.current) {
      const table = dataTableRef.current.querySelector("._DataTable");
      table.addEventListener("scroll", handleDebounceScroll);
      return () => {
        table.removeEventListener("scroll", handleDebounceScroll);
      };
    }
  }, [handleDebounceScroll]);

  const handleSort = ({ sortField: newSortField }) => {
    const { sortByColumn, sortByOrder } = shiftListSorting; // get from Redux

    let newSortOrder = "asc";
    if (sortByColumn === newSortField) {
      newSortOrder = sortByOrder === "asc" ? "desc" : "asc";
    }

    dispatch(
      updateShiftListSorting({
        sortByColumn: newSortField,
        sortByOrder: newSortOrder,
      }),
    );
    dispatch(deSelectAllListings());
    dispatch(
      updateShiftsPagination({
        ...shiftsPagination,
        currentPage: 1,
        isLastPage: false,
      }),
    );
    dispatch(setLoading(true));
    dispatch(fetchContractShifts());
  };

  return (
    <>
      <div ref={dataTableRef}>
        <DataTable
          columns={columns}
          data={contractShiftsLists}
          selectableRows
          onSort={handleSort}
          onSelectedRowsChange={(state) => {
            let selectedShifts = state.selectedRows.map((item) => {
              return item.shift_id;
            });
            dispatch(updateSelectedShifts(selectedShifts));
          }}
          clearSelectedRows={toggleSelected}
          noDataComponent={<TableNoContent />}
          progressPending={isShiftLoading}
          progressComponent={<TableLoader rows={3} />}
          className="_DataTable"
          fixedHeader
          fixedHeaderScrollHeight={"600px"}
        />
      </div>
    </>
  );
};

export default ShiftListing;
